<template>
  <component :is="shell">
    <div v-if="!embedded" class="page-head">
      <div>
        <h1>招投标分析</h1>
        <p class="sub">
          丢进招标文件，识别约束并匹配卡型。结果供参考，定稿请人工复核。支持 txt / md / pdf / docx，单文件 ≤ {{ maxMb }}MB。
        </p>
      </div>
      <button v-if="job && !composing" type="button" class="ghost" @click="startNew">新分析</button>
    </div>

    <div class="layout" :class="{ single: !hasUser, 'with-job': !!job }">
      <aside v-if="hasUser" class="history">
        <h3>分析记录</h3>
        <div v-if="history.length" class="hist-list">
          <button
            v-for="item in history"
            :key="item.id"
            type="button"
            class="hist-item"
            :class="{ on: job && job.id === item.id }"
            @click="openHistory(item.id)"
          >
            <span class="hist-title">{{ recordTitle(item) }}</span>
            <span class="hist-meta">
              <span :class="['tag', item.status]">{{ statusLabel(item.status) }}</span>
              <span v-if="item.status === 'done'">{{ item.full_count }} 款满分</span>
              <span v-if="item.created_at">{{ formatTime(item.created_at) }}</span>
            </span>
          </button>
        </div>
        <p v-else class="muted">还没有分析记录。提交一次后会列在这里。</p>
      </aside>

      <div class="main-col">
        <div v-if="composing && !embedded" class="composer">
          <p class="steps-hint">① 粘贴或上传 → ② 识别约束 → ③ 匹配卡型 → ④ 导出报告</p>
          <div class="upload-row">
            <label class="file">
              选择文件
              <input ref="fileInput" type="file" :accept="acceptAttr" @change="onFile" />
            </label>
            <span v-if="file" class="file-name">{{ file.name }}</span>
            <button v-if="file" type="button" class="ghost" @click="clearFile">清除</button>
            <button type="button" class="ghost" @click="fillSample">填入示例</button>
          </div>
          <textarea
            v-model="text"
            rows="8"
            placeholder="粘贴招标技术要求文本，或上传 txt / pdf / docx（至少 20 字）"
          />
          <div class="actions">
            <span v-if="!canSubmit" class="muted">请上传文件，或粘贴不少于 20 字</span>
            <button type="button" :disabled="loading || !canSubmit" @click="submit">
              {{ loading ? '提交中…' : '开始分析' }}
            </button>
          </div>
          <p v-if="file && !hasUser" class="login-hint">
            文件已上传。
            <router-link to="/account?mode=login">登录邮箱账号</router-link>
            后才能分析。
          </p>
          <CreditNext v-if="error && !job && gateStatus" :status="gateStatus" :message="error" />
          <p v-else-if="error && !job" class="error">{{ error }}</p>
          <p v-if="tip && !job" class="ok">{{ tip }}</p>
        </div>

        <section v-if="job" class="panel">
          <div class="status">
            <strong>#{{ job.id }}</strong>
            <span :class="['tag', job.status]">{{ statusLabel(job.status) }}</span>
            <span v-if="job.source_filename" class="muted">{{ job.source_filename }}</span>
            <template v-if="job.status === 'done'">
              <button type="button" :disabled="exporting" @click="exportFile('pdf')">
                {{ exporting === 'pdf' ? '导出中…' : '导出 PDF' }}
              </button>
              <button type="button" class="ghost" :disabled="exporting" @click="exportFile('docx')">Word</button>
              <button type="button" class="ghost" :disabled="exporting" @click="exportFile('pptx')">PPT</button>
              <router-link class="ghost linkish" :to="forumCompose()">讨论</router-link>
            </template>
            <button
              v-if="job.status === 'failed' || job.status === 'done'"
              type="button"
              class="ghost"
              :disabled="retrying"
              @click="retry"
            >{{ retrying ? '重试中…' : '重试' }}</button>
          </div>

          <QueryProcessRail
            v-if="processSteps.length"
            :steps="processSteps"
            :active="isBusy"
            :title="isBusy ? '正在规划并分析招标…' : '本次分析过程'"
          />
          <div v-else-if="isBusy" class="progress">
            <div class="progress-bar" />
            <p>{{ progressText }}</p>
          </div>

          <p v-if="job.error" class="error">{{ job.error }}</p>
          <CreditNext v-if="error && job && gateStatus" :status="gateStatus" :message="error" />
          <p v-else-if="error && job" class="error">{{ error }}</p>
          <p v-if="tip && job" class="ok">{{ tip }}</p>

          <div v-if="constraintChips.length" class="block">
                <h3>识别到的约束</h3>
                <div class="chips">
                  <span v-for="item in constraintChips" :key="item" class="chip info">{{ item }}</span>
                </div>
                <p v-if="job.constraints && job.constraints.qty_min" class="muted">
                  采购数量不少于 {{ job.constraints.qty_min }} 张，按整机方案配置，不参与单卡打分。
                </p>
                <details v-if="rawHits.length" class="hits">
                  <summary>原文命中（{{ rawHits.length }}）</summary>
                  <ul>
                    <li v-for="(hit, i) in rawHits" :key="i">{{ hit }}</li>
                  </ul>
                </details>
              </div>

              <template v-if="job.scores && job.scores.length">
                <p v-if="countSummary" class="summary">{{ countSummary }}</p>
                <div class="tabs">
                  <button
                    v-for="block in scoreBlocks"
                    :key="block.key"
                    type="button"
                    class="tab"
                    :class="{ on: resultTab === block.key }"
                    @click="resultTab = block.key"
                  >{{ block.title }} {{ block.rows.length }}</button>
                </div>
                <p class="legend">{{ tabLegend }}</p>
                <table v-if="activeRows.length">
                  <thead>
                    <tr>
                      <th>卡型</th>
                      <th>得分</th>
                      <th>分项</th>
                      <th class="col-reason">说明</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in activeRows" :key="row.card_id || row.name" :class="{ fullrow: row.is_full }">
                      <td>
                        <router-link
                          v-if="row.card_id"
                          class="card-link"
                          :to="{ path: '/', query: { q: row.name } }"
                        >{{ row.name }}</router-link>
                        <span v-else>{{ row.name }}</span>
                        <router-link
                          v-if="row.card_id && (resultTab === 'full' || resultTab === 'partial')"
                          class="discuss"
                          :to="forumCompose({ card_id: row.card_id, card_name: row.name, title: row.name })"
                        >讨论</router-link>
                      </td>
                      <td class="score" :class="{ full: row.is_full }">{{ row.score }} / {{ row.full_score }}</td>
                      <td>
                        <span
                          v-for="b in row.breakdown || []"
                          :key="b.key"
                          :class="['chip', b.pass ? 'ok' : 'no']"
                          :title="b.detail || ''"
                        >{{ b.label }}</span>
                      </td>
                      <td class="reasons col-reason">{{ (row.reasons || []).join('；') }}</td>
                    </tr>
                  </tbody>
                </table>
                <p v-else class="muted">该档暂无结果</p>
              </template>
              <p v-else-if="job.status === 'done'" class="muted">未匹配到可评分的卡型。</p>

          <RelatedDiscussions
            v-if="job.id"
            :bid-job-id="job.id"
            :bid-label="job.source_filename || ('招标 #' + job.id)"
            :card-ids="(job.scores || []).map((s) => s.card_id).filter(Boolean)"
            :keyword="job.source_filename || ''"
            :active="true"
          />
        </section>
      </div>
    </div>
  </component>
</template>

<script>
import AppShell from '../layouts/AppShell.vue'
import CreditNext from '../components/CreditNext.vue'
import RelatedDiscussions from '../components/RelatedDiscussions.vue'
import QueryProcessRail from '../components/QueryProcessRail.vue'
import {
  createBidJob,
  exportBidDocx,
  exportBidPdf,
  exportBidPptx,
  fetchBidAccepts,
  fetchMe,
  getBidJob,
  listBidJobs,
  retryBidJob
} from '../api/platformService'
import { readEvents } from '../api/sse'
import { notifyAuthChanged } from '../utils/authBus'
import {
  buildBidProcessSteps,
  finishBidProcessSteps
} from '../utils/queryProcessScript'

const STATUS = { pending: '排队中', running: '分析中', done: '已完成', failed: '失败' }
const TAB_LEGEND = {
  full: '满分：硬性指标都达标。优先国产只作提示，不把进口卡判成未达标。',
  partial: '部分达标：有扣分项，可作备选或说明偏离。',
  fail: '未达标：关键指标不满足招标要求。',
  unknown: '缺规格索引：百科暂无足够参数，无法自动判分，需人工核对。'
}
const SAMPLE =
  '本项目要求加速卡显存不小于 80GB，FP16 算力不低于 300 TFLOPS，整卡功耗不超过 700W，优先国产信创产品，支持 PCIe。技术评分满分 7 分。'

export default {
  name: 'BidWorkspace',
  components: { AppShell, RelatedDiscussions, CreditNext, QueryProcessRail },
  props: {
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      text: '',
      file: null,
      job: null,
      history: [],
      error: '',
      tip: '',
      loading: false,
      retrying: false,
      exporting: '',
      stream: null,
      hasUser: false,
      gateStatus: 0,
      composing: true,
      resultTab: 'full',
      maxMb: 8,
      acceptExt: ['.txt', '.md', '.pdf', '.docx'],
      processSteps: []
    }
  },
  computed: {
    shell() {
      return this.embedded ? 'div' : AppShell
    },
    canSubmit() {
      return Boolean(this.file) || this.text.trim().length >= 20
    },
    acceptAttr() {
      return this.acceptExt.join(',')
    },
    isBusy() {
      return this.job && (this.job.status === 'pending' || this.job.status === 'running')
    },
    progressText() {
      if (!this.job) return ''
      if (this.job.step) return this.job.step
      if (this.job.status === 'pending') return '已入队，正在等待分析…'
      return '正在识别约束并匹配卡型…'
    },
    constraintChips() {
      const c = this.job?.constraints || {}
      const items = []
      if (c.memory_gb_min != null) items.push(`显存 ≥ ${c.memory_gb_min} GB`)
      if (c.fp16_tflops_min != null) items.push(`FP16 ≥ ${c.fp16_tflops_min} T`)
      if (c.fp8_tflops_min != null) items.push(`FP8 ≥ ${c.fp8_tflops_min} T`)
      if (c.int8_tops_min != null) items.push(`INT8 ≥ ${c.int8_tops_min} TOPS`)
      if (c.tdp_w_max != null) items.push(`功耗 ≤ ${c.tdp_w_max} W`)
      if (c.form_factor) items.push(String(c.form_factor).toUpperCase())
      if (c.qty_min) items.push(`数量 ≥ ${c.qty_min}`)
      if (c.domestic_required) items.push('必须国产')
      else if (c.domestic_preferred) items.push('优先国产（不否决）')
      if (c.score_assumed) items.push(`未写满分，按 ${c.score_full} 分折算`)
      else if (c.score_full) items.push(`满分 ${c.score_full}`)
      return items
    },
    rawHits() {
      const hits = this.job?.constraints?.raw_hits
      return Array.isArray(hits) ? hits.filter(Boolean) : []
    },
    scoreBlocks() {
      const scores = this.job?.scores || []
      const groups = this.job?.groups || {}
      const pick = (key, pred) => groups[key] || scores.filter(pred)
      return [
        { key: 'full', title: '满分', rows: pick('full', (r) => r.tier === 'full' || r.is_full) },
        { key: 'partial', title: '部分达标', rows: pick('partial', (r) => r.tier === 'partial') },
        { key: 'fail', title: '未达标', rows: pick('fail', (r) => r.tier === 'fail') },
        { key: 'unknown', title: '缺规格索引', rows: pick('unknown', (r) => r.tier === 'unknown') }
      ]
    },
    activeRows() {
      const block = this.scoreBlocks.find((b) => b.key === this.resultTab)
      return block ? block.rows : []
    },
    countSummary() {
      const c = this.job?.groups?.counts
      if (!c || !c.total) return ''
      return `共评估 ${c.total} 款 · 满分 ${c.full} · 部分 ${c.partial} · 未达标 ${c.fail} · 缺索引 ${c.unknown}`
    },
    tabLegend() {
      return TAB_LEGEND[this.resultTab] || ''
    }
  },
  async mounted() {
    try {
      const accepts = await fetchBidAccepts()
      if (accepts?.extensions?.length) this.acceptExt = accepts.extensions
      if (accepts?.max_mb) this.maxMb = accepts.max_mb
    } catch (e) {
      /* keep defaults */
    }
    await this.checkUser()
    if (this.hasUser) await this.loadHistory()
    const jobId = this.$route.query?.job
    if (jobId && this.hasUser) await this.openHistory(Number(jobId))
  },
  beforeUnmount() {
    if (this.stream) this.stream.abort()
  },
  methods: {
    statusLabel(status) {
      return STATUS[status] || status
    },
    formatTime(iso) {
      if (!iso) return ''
      return String(iso).replace('T', ' ').slice(0, 16)
    },
    recordTitle(item) {
      if (item.source_filename) return item.source_filename
      if (item.preview) return item.preview
      return `文本 #${item.id}`
    },
    forumCompose(extra = {}) {
      const job = this.job
      const query = {
        compose: '1',
        bid_job_id: String(job.id),
        bid_label: job.source_filename || `招标任务 #${job.id}`,
        title: extra.title || `${job.source_filename || '招标'} 讨论`
      }
      if (extra.card_id) {
        query.card_id = String(extra.card_id)
        query.card_name = extra.card_name
      }
      return { path: '/forum', query }
    },
    syncProcess(serverStep = '', { done = false, clear = false } = {}) {
      if (clear) {
        this.processSteps = []
        return
      }
      const steps = buildBidProcessSteps(serverStep)
      this.processSteps = done ? finishBidProcessSteps(steps) : steps
    },
    startNew() {
      if (this.stream) this.stream.abort()
      this.job = null
      this.composing = true
      this.error = ''
      this.tip = ''
      this.resultTab = 'full'
      this.processSteps = []
      this.$router.replace({ path: '/bid', query: {} }).catch(() => {})
    },
    async checkUser() {
      try {
        const me = await fetchMe()
        this.hasUser = Boolean(me.user) || me.role === 'admin' || me.role === 'user'
      } catch (e) {
        this.hasUser = false
      }
    },
    async loadHistory() {
      try {
        this.history = (await listBidJobs()).jobs || []
      } catch (e) {
        this.history = []
      }
    },
    async openHistory(id) {
      this.error = ''
      this.tip = ''
      this.gateStatus = 0
      this.resultTab = 'full'
      this.composing = false
      this.$router.replace({ path: '/bid', query: { job: String(id) } }).catch(() => {})
      try {
        this.job = await getBidJob(id)
        if (this.job.status === 'pending' || this.job.status === 'running') {
          this.syncProcess(this.job.step || '已提交')
          this.watchJob(id)
        } else if (this.job.status === 'done') {
          this.syncProcess(this.job.step || '完成', { done: true })
        } else {
          this.syncProcess(this.job.step || '', { done: true })
        }
      } catch (err) {
        this.error = err.response?.data?.error || '加载失败'
        this.syncProcess('', { clear: true })
      }
    },
    onFile(e) {
      this.file = (e.target.files && e.target.files[0]) || null
    },
    clearFile() {
      this.file = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },
    fillSample() {
      this.text = SAMPLE
    },
    async submit() {
      if (!this.hasUser) {
        this.$router.push({ path: '/account', query: { mode: 'login' } })
        return
      }
      this.error = ''
      this.tip = ''
      this.gateStatus = 0
      this.loading = true
      try {
        let created
        if (this.file) {
          const fd = new FormData()
          fd.append('file', this.file)
          if (this.text.trim()) fd.append('text', this.text)
          created = await createBidJob(fd)
        } else {
          created = await createBidJob({ text: this.text })
        }
        this.composing = false
        this.tip = '已提交，分析消耗 3 积分。'
        this.resultTab = 'full'
        notifyAuthChanged()
        await this.loadHistory()
        await this.openHistory(created.id)
      } catch (err) {
        const status = err.response?.status
        this.gateStatus = status === 401 || status === 402 ? status : 0
        this.error = err.response?.data?.error || '提交失败'
        if (status === 401) this.hasUser = false
      } finally {
        this.loading = false
      }
    },
    watchJob(id) {
      if (this.stream) this.stream.abort()
      const controller = new AbortController()
      this.stream = controller
      readEvents(`/api/bid/jobs/${id}/events`, async (event) => {
        this.job = { ...(this.job || { id }), ...event }
        this.syncProcess(event.step || this.progressText)
        if (event.status !== 'done' && event.status !== 'failed') return
        this.job = await getBidJob(id)
        this.syncProcess(this.job.step || (event.status === 'done' ? '完成' : ''), { done: true })
        this.tip = event.status === 'done' ? '分析完成，可导出 PDF / Word / PPT。' : ''
        this.loadHistory()
      }, controller.signal).catch((err) => {
        if (err.name === 'AbortError') return
        this.error = err.message || '进度连接失败，可点左侧记录重新打开'
      })
    },
    async retry() {
      if (!this.job?.id) return
      this.error = ''
      this.tip = ''
      this.retrying = true
      try {
        const data = await retryBidJob(this.job.id)
        this.job = data
        this.composing = false
        this.tip = '已重新排队，重试不扣积分。'
        this.syncProcess(data.step || '已提交')
        this.watchJob(data.id)
        await this.loadHistory()
      } catch (err) {
        this.error = err.response?.data?.error || '重试失败'
      } finally {
        this.retrying = false
      }
    },
    async exportFile(kind) {
      if (!this.job?.id) return
      this.exporting = kind
      this.error = ''
      const run = { pdf: exportBidPdf, docx: exportBidDocx, pptx: exportBidPptx }[kind]
      try {
        const blob = await run(this.job.id)
        if (blob.type && blob.type.includes('application/json')) {
          const data = JSON.parse(await blob.text())
          throw new Error(data.error || '导出失败')
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `bid-report-${this.job.id}.${kind}`
        a.click()
        URL.revokeObjectURL(url)
      } catch (err) {
        this.error = err.message || err.response?.data?.error || '导出失败'
      } finally {
        this.exporting = ''
      }
    }
  }
}
</script>

<style scoped>
h1 { font-size: 34px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 8px; }
.page-head {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 16px;
}
.sub { color: #86909c; margin: 0; line-height: 1.6; max-width: 640px; font-size: 15px; }
.login-hint {
  margin: 10px 0 0;
  font-size: 13px;
  color: #4e5969;
}
.login-hint a { color: #165dff; }
.layout { display: flex; flex-direction: column; gap: 14px; }
.history {
  background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 12px;
}
.history h3 { margin: 0 0 10px; font-size: 14px; color: #1d2129; }
.hist-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  max-height: 280px;
  overflow: auto;
}
.layout.with-job .hist-list { max-height: 132px; }
.hist-item {
  width: 100%; text-align: left; border: 1px solid #e5e6eb; background: #fafbfc; color: #1d2129;
  border-radius: 12px; padding: 8px 10px; cursor: pointer; display: grid; gap: 4px;
}
.hist-item.on { border-color: #165dff; background: #e8f3ff; }
.hist-title {
  font-size: 13px; font-weight: 600; color: #1d2129; line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.hist-meta { display: flex; gap: 6px; align-items: center; font-size: 12px; color: #86909c; flex-wrap: wrap; }
.btn, button {
  border: 0; background: #165dff; color: #fff; border-radius: 12px; padding: 8px 14px; cursor: pointer; text-decoration: none;
}
button:disabled { opacity: 0.55; cursor: not-allowed; }
.ghost { background: #fff; color: #4e5969; border: 1px solid #e5e6eb; }
a.ghost.linkish { display: inline-block; padding: 6px 10px; border-radius: 12px; }
.composer { background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 14px; }
.steps-hint { margin: 0 0 12px; font-size: 13px; color: #86909c; }
textarea {
  width: 100%; border: 1px solid #e5e6eb; border-radius: 12px; padding: 10px; font: inherit; box-sizing: border-box;
}
.upload-row, .actions, .status, .chips, .tabs {
  display: flex; flex-wrap: wrap; gap: 8px; align-items: center;
}
.upload-row { margin-bottom: 10px; }
.actions { margin-top: 10px; justify-content: flex-end; }
.file { font-size: 13px; color: #4e5969; }
.file-name { font-size: 13px; color: #165dff; }
.panel { margin-top: 0; background: #fff; border: 1px solid #e5e6eb; border-radius: 16px; padding: 16px; }
.composer + .panel { margin-top: 14px; }
.tag { font-size: 12px; padding: 2px 8px; border-radius: 12px; background: #f2f3f5; }
.tag.done { background: #e8ffea; color: #188038; }
.tag.failed { background: #ffece8; color: #d3382c; }
.tag.running, .tag.pending { background: #e8f3ff; color: #165dff; }
.progress {
  margin: 12px 0; padding: 12px; background: #f7f8fa; border-radius: 12px;
}
.progress p { margin: 8px 0 0; font-size: 13px; color: #4e5969; }
.progress-bar {
  height: 4px; border-radius: 2px; background: linear-gradient(90deg, #bedaff, #165dff, #bedaff);
  background-size: 200% 100%; animation: slide 1.2s linear infinite;
}
@keyframes slide {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
.block h3 { margin: 12px 0 8px; font-size: 15px; }
.chips { margin: 0 0 8px; }
.hits { margin-top: 6px; font-size: 13px; color: #4e5969; }
.hits ul { margin: 8px 0 0; padding-left: 18px; }
.hits li { margin-bottom: 4px; }
.summary { margin: 8px 0; font-size: 13px; color: #4e5969; }
.legend { margin: 0 0 8px; font-size: 12px; color: #86909c; }
.tab {
  background: #fff; color: #4e5969; border: 1px solid #e5e6eb; padding: 6px 10px;
}
.tab.on { color: #165dff; border-color: #165dff; background: #e8f3ff; }
table { width: 100%; border-collapse: collapse; font-size: 14px; margin-top: 10px; }
th, td { border-top: 1px solid #e5e6eb; padding: 8px 6px; text-align: left; vertical-align: top; }
.score.full { color: #188038; font-weight: 700; }
.fullrow { background: #f6ffed; }
.reasons { color: #4e5969; font-size: 13px; }
.chip {
  display: inline-block; margin: 0 4px 4px 0; padding: 1px 6px; border-radius: 12px; font-size: 12px;
  background: #f2f3f5; color: #4e5969;
}
.chip.info { background: #e8f3ff; color: #165dff; }
.chip.ok { background: #e8ffea; color: #00b42a; }
.chip.no { background: #ffece8; color: #f53f3f; }
.card-link { color: #1d2129; text-decoration: none; font-weight: 600; }
.card-link:hover { color: #165dff; }
.discuss { margin-left: 8px; font-size: 12px; color: #165dff; text-decoration: none; }
.muted { color: #86909c; font-size: 13px; }
.error { color: #d3382c; }
.ok { color: #188038; font-size: 13px; }
.error a, .ok a { margin-left: 6px; color: #165dff; }
@media (max-width: 900px) {
  .col-reason { display: none; }
  .page-head { flex-direction: column; }
}
</style>
